<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('administration.standards') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <VTableLocal
        v-if="hasPermission('standards.view') && hasPermission('standardsFamilies.view') && hasPermission('analyses.types.view')"
        :iconHeader="hasPermission('standards.add')? 'add' : null"
        :items="displayedStandards"
        isSearchable
        :headers="tableHeaders"
        :itemActions="actions"
        :title="$t('administration.standards')"
        @iconAction="onClickAction"
        @click-icon-header="onClickAdd"
      />
    </template>
  </BoxWithTitle>
</template>

<script>
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import StandardForm from '@/components/forms/StandardForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';

export default {
    components: {
        BoxWithTitle,
        VTableLocal,
    },
    data() {
        return {
            standards: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'networkType',
                'componentType',
                'analysisType',
                'veryLow',
                'low',
                'minimum',
                'maximum',
                'high',
                'veryHigh',
            ].map(header => ({
                text: this.$t(`standards.labels.${header}`),
                value: header,
            }));
        },
        displayedStandards() {
            const standardsExceptCouponCat = this.standards.filter(
                s => !(s.analysisType?.type === analysisTypesConstant.TYPE_CORROSION
                    || s.analysisType?.type === analysisTypesConstant.TYPE_LEGIONELLA),
            );
            return standardsExceptCouponCat.map(standard => {
                const {
                    id, networkType, componentType, analysisType, norm,
                } = standard;
                const {
                    veryLow, low, minimum, maximum, high, veryHigh,
                } = norm;

                return {
                    id,
                    networkType: networkType ? networkType.name : this.$t('forms.labels.any'),
                    componentType: componentType ? componentType.name : this.$t('forms.labels.any'),
                    analysisType: analysisType.name[this.userLang],
                    veryLow,
                    low,
                    minimum,
                    maximum,
                    high,
                    veryHigh,
                };
            });
        },
        actions() {
            const actions = [];
            if (this.hasPermission('standards.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('standards.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        analysisTypes() {
            return this.standards.map(({ analysisType }) => analysisType.id);
        },
    },
    async created() {
        await this.getStandards();
    },
    methods: {
        async getStandards() {
            this.loading = true;
            const queryParams = { standardFamily: this.$route.params.standardFamilyId };
            const { data } = await this.$rq.getStandards(queryParams);
            this.standards = data.standards.filter(standard => standard.analysisType !== undefined);
            this.loading = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('standards.addStandard'),
                component: Vue.component('StandardForm', StandardForm),
                payload: {
                    standards: this.standards,
                },
                onConfirm: requestPayload => this.addStandardRequest(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (action.mode === 'edit') {
                const standard = await this.standards.find(({ id }) => id === item.id);
                const norm = {};
                if (item.veryLow) norm.veryLow = item.veryLow.toString();
                if (item.low) norm.low = item.low.toString();
                if (item.minimum) norm.minimum = item.minimum.toString();
                if (item.maximum) norm.maximum = item.maximum.toString();
                if (item.high) norm.high = item.high.toString();
                if (item.veryHigh) norm.veryHigh = item.veryHigh.toString();
                const standardData = cloneDeep(standard);
                standardData.norm = norm;

                this.$root.$emit('showModal', {
                    title: this.$t('standards.editStandard'),
                    component: Vue.component('StandardForm', StandardForm),
                    validateAtInitialization: true,
                    payload: {
                        standard: standardData,
                        isEdition: true,
                        standards: this.standards,
                    },
                    onConfirm: requestPayload => this.updateStandardRequest(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('standards.removeStandard'),
                    component: Vue.component('confirmRemoveStandardForm', ConfirmForm),
                    payload: { msg: this.$t('standards.confirmRemoveStandard') },
                    onConfirm: () => this.deleteStandardRequest(item.id),
                    isForm: false,
                });
            }
        },
        preparePayload(payload) {
            const requestPayload = cloneDeep(payload);
            if (requestPayload.networkType === null) {
                delete requestPayload.networkType;
            }
            if (requestPayload.componentType === null) {
                delete requestPayload.componentType;
            }

            Object.keys(requestPayload.norm).forEach(key => {
                if (!requestPayload.norm[key] || !requestPayload.norm[key].length) {
                    delete requestPayload.norm[key];
                }
            });
            return requestPayload;
        },
        async addStandardRequest(payload) {
            const requestPayload = this.preparePayload(payload);
            requestPayload.standardFamily = this.$route.params.standardFamilyId;

            await this.$rq.addStandard(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardCreated'),
            });
            await this.getStandards();
        },
        async updateStandardRequest(standardId, payload) {
            const requestPayload = this.preparePayload(payload);
            await this.$rq.updateStandard(standardId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardModified'),
            });
            await this.getStandards();
        },
        async deleteStandardRequest(standardId) {
            await this.$rq.deleteStandard(standardId);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardRemoved'),
            });
            await this.getStandards();
        },
    },
};
</script>
